import { useState, useEffect, useRef } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTranslation } from 'react-i18next';
import { Building, BuildingListing } from '@root/types';
import { Row, Col } from '@components/antd';
import { PARAMETERS } from '@root/tracking/constants';
import { isEmpty } from 'lodash/fp';
import routes from '@root/routes';
import cs from 'classnames';
import { AddToSavedItemsButton, ContactChooser, OnlyInDesktopTablet } from '@components/shared';
import Location from '@components/shared/Location';
import Transportation from '@components/shared/Transportation';
import Divider from '@components/shared/Divider';
import Breadcrumbs from '@components/shared/Breadcrumbs';
import Bartender from '@components/shared/Bartender';
import { Tooltip } from 'antd';
import PageLayout from '../Layout';
import Certifications from './Certifications';
import VideoTour from '../Listing/VideoTour';
import Title from './Title';
import BasicInfo from './BasicInfo';
import s from './Building.module.less';
import Amenities from './Amenities';
import StaticMapImage from './StaticMapImage';
import AvailableSpaces from './AvailableSpaces';
import Images from './Images';

type Props = {
  building: Building;
  listingClicked: (listing: BuildingListing, tab: string) => void;
  contactClicked: () => void;
  hasCurrentUser: boolean;
  onSave: (id: string, slug: string) => Promise<void>;
  onUnsave: (id: string, slug: string) => Promise<void>;
  isSaved: boolean;
  isPreview: boolean;
};

const videoSourceContent = {
  three_d_tour: PARAMETERS.spotlightTour,
  video: PARAMETERS.buildingVideo,
};

const Layout = ({
  building,
  listingClicked,
  contactClicked,
  hasCurrentUser,
  onSave,
  onUnsave,
  isSaved,
  isPreview,
}: Props) => {
  const [hasRendered, setHasRendered] = useState(false);
  useEffect(() => setHasRendered(true), []);

  const flags = useFlags();
  const { t } = useTranslation('building');

  const locationSection = useRef<HTMLDivElement | null>(null);
  const handleScrollToLocation = () => {
    if (locationSection.current) {
      locationSection.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  };

  const handleSaveBuilding = (shouldBeSaved: boolean) => {
    if (shouldBeSaved) {
      onSave(building.id, building.slug);
    } else {
      onUnsave(building.id, building.slug);
    }
  };

  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  return (
    <PageLayout data-testid="buildingPage">
      {flags['move-breadcrumbs-to-layout'] ? (
        <OnlyInDesktopTablet>
          <Breadcrumbs className={s.breadcrumbs} />
        </OnlyInDesktopTablet>
      ) : null}
      <Bartender
        drinks={[
          {
            url: routes.admin.building(building.id),
            tooltipText: 'Admin Page of the Building',
            icon: 'buildings-multiple',
          },
        ]}
        className={s.bartender}
      />
      <div className={s.titleContainer}>
        <Title
          primaryName={building.primaryName}
          secondaryName={building.secondaryName}
          cityLocalityRegion={building.cityLocalityRegion}
          countryCode={building.countryCode}
          neighborhood={building.neighborhood}
        />
        {hasCurrentUser && (
          <div className={s.saveButtonContainer}>
            <Tooltip trigger={['hover', 'click']} placement="bottom" title={t('saveTooltip')}>
              <span>
                <AddToSavedItemsButton
                  isSaved={isSaved}
                  onClick={handleSaveBuilding}
                  className={s.saveButton}
                />
              </span>
            </Tooltip>
          </div>
        )}
      </div>
      {hasRendered && (
        <Images
          firstPhotoOrientation={building.firstPhotoOrientation}
          photos={building.photos}
          video={building.video}
          tourEmbedUrl={building.tourEmbedUrl}
          sourcePage={PARAMETERS.buildingPage}
          sourceContent={PARAMETERS.buildingPageGallery}
          imagesZipUrl={routes.api.buildingImagesZipUrl(building.slug)}
          onLightboxOpened={() => setIsLightboxOpen(true)}
          onLightboxClosed={() => setIsLightboxOpen(false)}
        />
      )}
      <Row gutter={16}>
        <Col lg={{ span: 16, order: 1 }} md={{ span: 18, order: 1 }} sm={{ span: 24, order: 2 }}>
          <BasicInfo building={building} />
        </Col>
        <Col
          className={cs(
            isEmpty(building.notableTenants) && !building.description && s.mapPreviewContainer,
          )}
          lg={{ span: 8, order: 2 }}
          md={{ span: 6, order: 2 }}
          sm={0}
        >
          <StaticMapImage
            address={building.formattedAddress}
            onScrollToLocation={handleScrollToLocation}
            transitOptions={building.transitOptions}
            className={s.mapImage}
          />
        </Col>
      </Row>
      <Row block>
        <AvailableSpaces listingClicked={listingClicked} buildingSlug={building.slug} />
      </Row>
      <Row gutter={16} className={s.certificationTourRow}>
        <Col lg={16} md={16} sm={24}>
          <Amenities amenities={building.amenities} />
          <Certifications
            leedCertification={building.leedCertification}
            wiredCertification={building.wiredCertification}
          />
        </Col>
        <Col lg={8} md={8} sm={24}>
          <ContactChooser
            skipInquiry
            listingAgents={building.listingAgents}
            emailSubjectName={[building.primaryName, building.secondaryName].join(' ')}
            title={t('tourThisBuilding')}
            trackContactClick={contactClicked}
            isPreview={isPreview}
          />
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <Divider className={s.divider} />
          <VideoTour
            video={building.video}
            sourcePage={PARAMETERS.buildingPage}
            sourceContent={
              flags['media-analytics']
                ? PARAMETERS.spotlightTour
                : videoSourceContent[building.virtualTourSpotlight]
            }
            tourEmbedUrl={building.tourEmbedUrl}
            subtitle={false}
            virtualTourSpotlight={building.virtualTourSpotlight}
            isVisible={!isLightboxOpen}
          />
          <Divider className={cs(s.divider, s.withExtraPadding)} />
          <div ref={locationSection}>
            <Location address={building.formattedAddress} />
            <Transportation transitOptions={building.transitOptions} />
          </div>
        </Col>
      </Row>
    </PageLayout>
  );
};

export default Layout;
